import { DownloadSimpleIcon } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import { PlayIcon } from "@phosphor-icons/react/dist/csr/Play";
import { useNavigate } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { toast } from "@volca/ui/lib/toast";
import { getErrorMessage } from "@/core/api/errors";
import { useBusiness } from "@/core/business";
import { sortDateDescending } from "@/core/utils/date";
import { PAYROLL_RUN_STATUS } from "@/payroll/constants";
import { getPayBreakdownPath } from "@/payroll/pay-breakdown/routes";
import { EnrichedPayPeriod, PayrollRunStatus } from "@/payroll/payroll.d";
import { downloadFile } from "@/payroll/utils/download-file";
import {
  canTriggerNewRun,
  isPayPeriodLocked,
} from "@/payroll/utils/period-helpers";
import { PayPeriodLockedMark } from "./pay-period-locked";
import { PayrollRunStatusBadge } from "./payroll-run-status-badge";
import {
  PAYROLL_RUN_TYPE,
  useCreatePayPeriod,
  useGetPayPeriodDetail,
  useTriggerPayrollRun,
} from "./query";
import { RelativeTime } from "./relative-time";
import { RunElapsedTime } from "./run-elapsed-time";

interface PayPeriodCardProps {
  period: EnrichedPayPeriod;
  mode?: "script" | "icm";
  disabledReason?: string;
}

export const PayPeriodCard = ({
  period,
  mode = "script",
  disabledReason,
}: PayPeriodCardProps) => {
  const navigate = useNavigate();
  const { businessId, selectedBusiness } = useBusiness();
  const isPayrollScriptEnabled =
    !!selectedBusiness?.payroll_script_config?.enabled;

  const isIcmMode = mode === "icm";
  const isLocked = isPayPeriodLocked(period);

  // Resolve mode-specific period fields — each mode tracks its own run counts and status
  const { periodLatestStatus, periodRunsCount, periodLatestRunAt } = isIcmMode
    ? {
        periodLatestStatus: period.latest_icm_run_status,
        periodRunsCount: period.icm_runs_count,
        periodLatestRunAt: period.latest_icm_run_at,
      }
    : {
        periodLatestStatus: period.latest_run_status,
        periodRunsCount: period.runs_count,
        periodLatestRunAt: period.latest_run_at,
      };

  const shouldFetchDetail =
    !isIcmMode && period.existsInDb && periodRunsCount > 0;

  const { data: detailData } = useGetPayPeriodDetail(businessId, period.id, {
    enabled: shouldFetchDetail,
  });

  const sortedRuns = detailData?.payroll_runs
    ? [...detailData.payroll_runs].sort((a, b) =>
        sortDateDescending(a.created_at, b.created_at),
      )
    : [];

  const latestRun = isIcmMode ? undefined : sortedRuns[0];
  const latestRunStatus: PayrollRunStatus | null = isIcmMode
    ? periodLatestStatus
    : periodRunsCount > 0
      ? latestRun?.status || period.latest_run_status
      : null;

  const { mutate: createPeriod, isPending: isCreatingPeriod } =
    useCreatePayPeriod(businessId);
  const { mutate: triggerRun, isPending: isTriggeringRun } =
    useTriggerPayrollRun(businessId, period.id);

  const isTriggering = isCreatingPeriod || isTriggeringRun;
  const hasDeferredRun = latestRunStatus === PAYROLL_RUN_STATUS.deferred;
  const hasActiveRun =
    latestRunStatus === PAYROLL_RUN_STATUS.pending ||
    latestRunStatus === PAYROLL_RUN_STATUS.processing;

  const handleTriggerRun = () => {
    if (!isIcmMode) return;

    if (!period.existsInDb) {
      createPeriod(
        {
          start_date: period.start_date,
          end_date: period.end_date,
        },
        {
          onSuccess: () => {
            toast.success("Payroll request submitted");
          },
          onError: (error: unknown) => {
            toast.error("Failed to start payroll run", {
              description: getErrorMessage(error),
            });
          },
        },
      );
    } else {
      triggerRun(
        {
          run_type: PAYROLL_RUN_TYPE.icm,
        },
        {
          onSuccess: () => {
            toast.success("Payroll request submitted");
          },
          onError: (error: unknown) => {
            toast.error("Failed to start payroll run", {
              description: getErrorMessage(error),
            });
          },
        },
      );
    }
  };

  const handleDownload = () => {
    if (
      latestRun?.result_file_url &&
      latestRun.result_file_metadata?.filename
    ) {
      downloadFile(
        latestRun.result_file_url,
        latestRun.result_file_metadata.filename,
      );
    }
  };

  const canTrigger =
    !disabledReason &&
    (!period.existsInDb || canTriggerNewRun(period, latestRunStatus));
  const showLoadingIcon = isTriggering || hasActiveRun;
  const isRequestPayrollDisabled =
    !canTrigger || showLoadingIcon || !!hasDeferredRun;
  const hasRuns = periodRunsCount > 0;
  const latestRunTimestamp = latestRun?.created_at || periodLatestRunAt;
  const hasDownloadableRun =
    !isIcmMode &&
    latestRun?.status === PAYROLL_RUN_STATUS.completed &&
    !!latestRun.result_file_url;
  const showRequestButton =
    isIcmMode && !isLocked && (!hasRuns || showLoadingIcon || hasDeferredRun);
  const requestPayrollButtonText = hasRuns
    ? "Request Payroll Again"
    : "Request Payroll";

  return (
    <Card className="w-full max-w-4xl gap-0 py-0">
      <Stack align="start" gap={4} className="w-full p-4">
        <Stack
          className="w-full"
          direction="row"
          align="center"
          justify="between"
          gap={2}
        >
          <Stack direction="row" align="center" gap={2}>
            {hasRuns ? (
              <>
                {latestRunStatus && (
                  <PayrollRunStatusBadge status={latestRunStatus} />
                )}
                <Stack align="start" gap={0} className="min-w-0">
                  {latestRun && (
                    <Text className="wrap-break-word">
                      Requested by{" "}
                      {latestRun.user_display_name || "Volca Admin"}
                    </Text>
                  )}
                  {latestRunTimestamp && (
                    <RelativeTime
                      timestamp={latestRunTimestamp}
                      className="text-subtle"
                    />
                  )}
                </Stack>
              </>
            ) : (
              <Text className="text-subtle" data-testid="payroll-request-copy">
                {isLocked || !isIcmMode
                  ? "No payroll results for this period."
                  : "Click the button to request your payroll."}
              </Text>
            )}
          </Stack>

          <Stack direction="row" align="center" justify="end" gap={2}>
            {(hasActiveRun || hasDeferredRun) &&
              isPayrollScriptEnabled &&
              latestRun?.created_at && (
                <RunElapsedTime createdAt={latestRun.created_at} />
              )}
            <Stack direction="row" align="center" gap={2}>
              {isLocked && <PayPeriodLockedMark withLabel className="mr-1" />}
              {isIcmMode && hasRuns && !showLoadingIcon && !hasDeferredRun && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    navigate(getPayBreakdownPath(period.start_date))
                  }
                >
                  View
                </Button>
              )}
              {showRequestButton ? (
                <Tooltip>
                  <TooltipTrigger render={<span />}>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleTriggerRun}
                      disabled={isRequestPayrollDisabled}
                      loading={showLoadingIcon || hasDeferredRun}
                      loadingLabel={`${requestPayrollButtonText}, in progress`}
                    >
                      {!showLoadingIcon && !hasDeferredRun && (
                        <PlayIcon className="h-4 w-4" />
                      )}
                      {requestPayrollButtonText}
                    </Button>
                  </TooltipTrigger>
                  {disabledReason && (
                    <TooltipContent>{disabledReason}</TooltipContent>
                  )}
                </Tooltip>
              ) : null}
              {hasDownloadableRun && (
                <Button variant="default" size="sm" onClick={handleDownload}>
                  <DownloadSimpleIcon data-icon="inline-start" />
                  Download
                </Button>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};
